import { useState, useEffect, useCallback, RefObject } from "react";

interface UseInfiniteScrollPaginationOptions {
  /** Distance from bottom/end (in px) to trigger next page load. Default: 400 */
  threshold?: number;
  /** Number of items per page. Default: 10 */
  pageSize?: number;
  /** Whether data is currently being fetched (prevents duplicate triggers). */
  loading?: boolean;
  /**
   * Optional scroll container ref. When provided, the scroll listener is
   * attached to that element instead of `window`.  Supports both vertical
   * and horizontal overflow containers.
   */
  scrollContainerRef?: RefObject<HTMLElement>;
}

interface UseInfiniteScrollPaginationReturn {
  currentPage: number;
  pageSize: number;
  hasMore: boolean;
  /** Call after a successful fetch with the total number of pages from the API */
  setTotalPages: (totalPages: number) => void;
  /** Reset pagination (e.g. when filters change) */
  reset: () => void;
}

/**
 * Reusable hook that increments `currentPage` when the user scrolls
 * near the bottom of the page, similar to infinite-scroll behaviour.
 *
 * Usage:
 * ```
 * const { currentPage, pageSize, hasMore, setTotalPages, reset } =
 *   useInfiniteScrollPagination({ threshold: 400, pageSize: 10, loading });
 *
 * useEffect(() => {
 *    Fn()
 * }, [currentPage])
 *
 * useEffect(()=>{
 * reset()
 * setVar([])
 * },[...filters])
 * ```
 */
const useInfiniteScrollPagination = (
  options: UseInfiniteScrollPaginationOptions = {},
): UseInfiniteScrollPaginationReturn => {
  const {
    threshold = 400,
    pageSize = 10,
    loading = false,
    scrollContainerRef,
  } = options;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPagesState] = useState<number>(Infinity);
  const [hasMore, setHasMore] = useState<boolean>(true);

  // Expose a setter that also updates `hasMore`
  const setTotalPages = useCallback((tp: number) => {
    setTotalPagesState(tp);
  }, []);

  // Derive hasMore from page vs totalPages
  useEffect(() => {
    setHasMore(currentPage < totalPages);
  }, [currentPage, totalPages]);

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => {
      if (loading || !hasMore) return;

      const el = scrollContainerRef?.current;
      if (el) {
        // Container-based: detect proximity to the scroll end (horizontal or vertical)
        const nearHorizontalEnd =
          el.scrollLeft + el.clientWidth >= el.scrollWidth - threshold;
        const nearVerticalEnd =
          el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
        if (nearHorizontalEnd || nearVerticalEnd) {
          setCurrentPage((prev) => prev + 1);
        }
      } else {
        // Window-based (default)
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        if (scrollTop + windowHeight >= documentHeight - threshold) {
          setCurrentPage((prev) => prev + 1);
        }
      }
    };

    const target = scrollContainerRef?.current ?? window;
    // Passive: the handler never calls preventDefault, so this keeps scrolling
    // off the main thread.
    target.addEventListener("scroll", handleScroll, { passive: true });
    return () => target.removeEventListener("scroll", handleScroll);
  }, [loading, hasMore, threshold, scrollContainerRef]);

  // Reset helper (when filters / search change)
  const reset = useCallback(() => {
    setCurrentPage(1);
    setTotalPagesState(Infinity);
    setHasMore(true);
  }, []);

  return { currentPage, pageSize, hasMore, setTotalPages, reset };
};

export default useInfiniteScrollPagination;
